import { memo } from 'react';
import { Button, Card } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { CircleAlert, KeyRound, Layers, Mail, MapPin, Send, UserCheck } from 'lucide-react';

function SummaryLine({ icon: Icon, children, action }) {
  return (
    <div className="flex items-start justify-between gap-4 text-sm text-content-secondary">
      <div className="flex items-start gap-3">
        <Icon size={16} className="mt-0.5 shrink-0 text-content-muted" aria-hidden />
        <p>{children}</p>
      </div>
      {action}
    </div>
  );
}

/**
 * Summary of what will happen when the people are added: ready count, access per studio,
 * the batch note, existing members (profile-only update) and the rows left out. Each link
 * returns to the review step already filtered.
 *
 * @param {object} props
 * @param {object} props.summary From `selectCheckSummary`.
 * @param {string} props.batchLabel e.g. `Imported Oct 13 · roster.xlsx`.
 * @param {() => void} props.onAssignAccess
 * @param {() => void} props.onViewUpdates
 * @param {() => void} props.onViewRows
 */
function ImportSummaryCard({ summary, batchLabel, onAssignAccess, onViewUpdates, onViewRows }) {
  const { t } = useTranslation('people-access');
  const { ready, updates, excluded, byStudio, withoutStudio, locations, alreadyInvited } = summary;
  const studios = Object.entries(byStudio).map(([id, count]) =>
    t('import.check.summary.studioItem', { count, studio: t(`import.check.studios.${id}`, { defaultValue: id }) }),
  );

  return (
    <Card className="flex flex-col gap-3 p-5">
      <h2 className="text-lg font-bold text-content">
        {ready === 0 && updates > 0
          ? t('import.check.titleUpdates', { count: updates })
          : t('import.check.title', { count: ready })}
      </h2>
      {studios.length > 0 ? (
        <SummaryLine icon={Layers}>{t('import.check.summary.access', { items: studios.join(', ') })}</SummaryLine>
      ) : null}
      {withoutStudio > 0 ? (
        <SummaryLine
          icon={KeyRound}
          action={
            <Button variant="ghost" size="sm" onClick={onAssignAccess}>
              {t('import.check.summary.assignAccess')}
            </Button>
          }
        >
          {withoutStudio === ready
            ? t('import.check.summary.noRoleAll')
            : t('import.check.summary.noRoleSome', { count: withoutStudio })}
        </SummaryLine>
      ) : null}
      <SummaryLine icon={Mail}>{t('import.check.summary.batchNote', { batch: batchLabel })}</SummaryLine>
      {locations.length > 0 ? (
        <SummaryLine icon={MapPin}>{t('import.check.summary.locations', { items: locations.join(', ') })}</SummaryLine>
      ) : null}
      {updates > 0 ? (
        <SummaryLine
          icon={UserCheck}
          action={
            <Button variant="ghost" size="sm" onClick={onViewUpdates}>
              {t('import.check.summary.viewUpdates')}
            </Button>
          }
        >
          {t('import.check.summary.updates', { count: updates })}
        </SummaryLine>
      ) : null}
      {alreadyInvited > 0 ? (
        <SummaryLine icon={Send}>{t('import.check.summary.alreadyInvited', { count: alreadyInvited })}</SummaryLine>
      ) : null}
      {excluded > 0 ? (
        <SummaryLine
          icon={CircleAlert}
          action={
            <Button variant="ghost" size="sm" onClick={onViewRows}>
              {t('import.check.summary.viewRows')}
            </Button>
          }
        >
          {t('import.check.summary.excluded', { count: excluded })}
        </SummaryLine>
      ) : null}
    </Card>
  );
}

export default memo(ImportSummaryCard);
